import { graphql } from "gatsby"
import React, { useContext } from "react"
import Col from "react-bootstrap/Col"
import Row from "react-bootstrap/Row"
import { useTranslation } from "react-i18next"

import Button from "@atoms/Button/Button"
import Shape from "@atoms/Shape/Shape"
import Layout from "@components/layout.js"
import SEO from "@components/seo"
import { SiteContext } from "@context/Provider"
import FormOfficeSpace from "@forms/FormOfficeSpace/FormOfficeSpace"
import "@lang/i18n"
import i18nWrapper from "@lang/i18nWrapper"
import Breadcrumb from "@molecules/Breadcrumb/Breadcrumb"
import ServiceDescriptionListItem from "@molecules/ServiceDescriptionListItem/ServiceDescriptionListItem"
import ServiceDescriptionList from "@organisms/ServiceDescriptionList/ServiceDescriptionList"

import styles from "./CampusServices.module.scss"

const Campus = (props) => {
  const {
    data: { single: campus },
  } = props

  const {
    title,
    seoTitle,
    seoHeadJson,
    uri,
    wpParent,
    campusServicesExcerpt,
    campusServiceDescriptions,
    language,
    translations,
  } = campus

  const currentLanguage = language.slug

  // Context
  const context = useContext(SiteContext)
  context.currentLanguage = currentLanguage

  const { t } = useTranslation()

  const languageRoot =
    currentLanguage === "en" ? "/" : "/" + currentLanguage + "/"

  // Services

  const serviceListItems = []

  if (campusServiceDescriptions) {
    campusServiceDescriptions.forEach((item, i) => {
      const {
        aspectRatioImage,
        service,
        serviceTextHTML,
        serviceHeadingOverride,
      } = item || {}

      const filteredHTML = serviceTextHTML
        ? serviceTextHTML
            .replace("https://tpkisumufrontend.gtsb.io/", "/")
            .replace("https://cms.technopolisglobal.com/", "/")
        : ""

      const title = serviceHeadingOverride
        ? serviceHeadingOverride
        : service?.title

      if (filteredHTML && title) {
        serviceListItems.push(
          <ServiceDescriptionListItem
            key={i}
            image={aspectRatioImage}
            icon={service?.serviceIcon}
            title={title}
            content={filteredHTML}
          />
        )
      }
    })
  }

  return (
    <Layout
      translations={translations}
      currentLanguage={currentLanguage}
      lang={language.locale}
    >
      <SEO
        title={seoTitle}
        uri={uri}
        lang={language.locale}
        headJson={seoHeadJson}
        translations={translations}
      />

      <div className={`${styles.widgetContainer} make-full-width-on-mobile`}>
        <div className={styles.widget}>
          <div className="container">
            <div className={styles.content}>
              <div className={styles.breadcrumb}>
                <Breadcrumb
                  ancestors={[
                    {
                      title: t("Home"),
                      url: languageRoot,
                    },
                    {
                      title: campus.areas ? campus.areas.nodes[0]?.name : null,
                      url: campus.areas ? campus.areas.nodes[0]?.uri : null,
                    },
                    {
                      title: wpParent?.node?.title,
                      url: wpParent?.node?.uri,
                    },
                  ]}
                  current={t("Services")}
                />
              </div>
              <div className="">
                <h1 className={styles.widgetTitle}>{title}</h1>
                <p className="type-h5 type-balanced">{campusServicesExcerpt}</p>
              </div>
              {serviceListItems.length > 0 && (
                <div className="base-margin-top base-margin-bottom-large">
                  <ServiceDescriptionList>
                    {serviceListItems}
                  </ServiceDescriptionList>
                </div>
              )}
              {wpParent?.node?.uri && (
                <div className="base-margin-top-large base-margin-bottom">
                  <Row>
                    <div className="col-lg-4">
                      <Button
                        href={wpParent.node.uri}
                        variant="primary"
                        size="full-width"
                      >
                        {t("Back to campus page")}
                      </Button>
                    </div>
                  </Row>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="contact-encouragement section-break-out" id="contact">
        <div className="container">
          <Row>
            <Col md={{ span: 10, offset: 1 }} lg={{ span: 8, offset: 2 }}>
              <div className="contact-encouragement-container">
                <div className="form-introduction form-introduction-small">
                  <h2 className="type-h2">{t("Need a new office space?")}</h2>
                  <p className="extra-line-height">
                    {t("Fill in the form and we'll be in touch!")}
                  </p>
                </div>
                <FormOfficeSpace
                  currentLanguage={currentLanguage}
                  city={
                    campus.areas
                      ? campus.areas.nodes[0]?.name
                      : campus.campusLocation.city
                  }
                />
              </div>
            </Col>
          </Row>
        </div>
        <Shape type="triangle-bottom-left-large" color="light-beige" x="1.2" />
      </div>
    </Layout>
  )
}

export default i18nWrapper("Campus")(Campus)

export const query = graphql`
  query SINGLE_CAMPUS_SERVICE($id: String!) {
    single: wpCampus(id: { eq: $id }) {
      title
      seoTitle
      seoHeadJson
      uri
      language {
        slug
        locale
      }
      translations {
        title
        uri
        language {
          slug
          locale
        }
      }
      areas {
        nodes {
          slug
          name
          uri
        }
      }
      campusServicesExcerpt
      campusServiceDescriptions {
        service {
          title
          serviceIcon
        }
        serviceTextHTML
        serviceHeadingOverride
        aspectRatioImage {
          sourceUrl
          mediaDetails {
            width
            height
          }
        }
      }
      wpParent {
        node {
          id
          uri
          ... on WpCampus {
            title
          }
        }
      }
    }
  }
`
